import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalActionBar from 'components/modal-action-bar';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import useContactsVariant, { resolveDialogFullScreen } from 'views/contacts/hooks/use-contacts-variant';

const useStyles = makeStyles((theme: any) => ({
  title: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  content: {
    padding: theme.spacing(2, 2, 1),
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(2, 3, 1) },
  },
}));

type containerModalProps = {
  open: boolean;
  modalTitle: string;
  onClose: () => void
  onSave: () => void
  onCancel: () => void;
  loadingSaveButton?: boolean;
  disableSavebutton?: boolean;
  submitText?: string;
  submitIcon?: IconProp;
  maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl" | undefined
  children: React.ReactNode;
}

// Shared shell for the contact edit dialogs: full screen on phones, DESIGN.md §5 action bar,
// and locked (no Escape/backdrop close) while the save request is in flight.
const ContainerModal: FC<containerModalProps> = ({
  open,
  onClose,
  children,
  onSave,
  onCancel,
  modalTitle,
  loadingSaveButton = false,
  disableSavebutton = false,
  submitText,
  submitIcon,
  maxWidth,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const fullScreen = resolveDialogFullScreen(useContactsVariant());

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !loadingSaveButton) {
          onClose();
        }
      }}
      disableEscapeKeyDown={loadingSaveButton}
      fullWidth
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      aria-labelledby="contact-modal-title"
    >
      <DialogTitle id="contact-modal-title" className={classes.title}>
        {modalTitle}
      </DialogTitle>
      <DialogContent className={classes.content}>
        {children}
      </DialogContent>
      <ModalActionBar
        onCancel={onCancel}
        submitType="button"
        submitText={submitText ?? t('common:save')}
        submitIcon={submitIcon}
        onSubmit={onSave}
        isSubmitting={loadingSaveButton}
        submitDisabled={disableSavebutton}
      />
    </Dialog>
  );
};

export default ContainerModal;
